import React, { memo, useCallback, useMemo, useEffect } from 'react';
import { Doc, DocTypes } from '@app/models';
import { Button } from '../../components/ui/button';
import { Input } from 'components/ui/input';
import { Card } from 'components/ui/card';
import { useForm } from '../hooks';
import { date, object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@app/components';

export interface FormDocValues {
  name: string;
  price: string;
  description: string;
  endDate: Date | null;
}

interface Props {
  onSubmit: (doc: Partial<Doc>) => void;
  type: DocTypes;
  savedValues?: Partial<FormDocValues>;
  setValues?: (values: Partial<FormDocValues>) => void;
}

const TEXT_MIN = 2;
const TEXT_MAX = 50;

function FormDoc(props: Props) {
  const { onSubmit, type, savedValues, setValues: setValuesProps } = props;
  const { t } = useTranslation();
  const { values, onChange, errors, validate, setValues } =
    useForm<FormDocValues>({
      name: savedValues?.name ?? '',
      price: savedValues?.price ?? '',
      description: savedValues?.description ?? '',
      endDate: savedValues?.endDate ?? null,
    });

  useEffect(() => {
    if (setValuesProps) {
      setValuesProps({
        name: values.name,
        price: values.price,
        description: values.description,
        endDate: values.endDate,
      });
    }
  }, [values, setValuesProps]);

  const onChangePrice = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, '');
      onChange(digits, 'price');
    },
    [onChange]
  );

  const onClickSubmit = useCallback(async () => {
    try {
      // Trim text fields before validation
      const trimmedName = values.name.trim();
      const trimmedDescription = values.description.trim();
      const trimmedPrice =
        typeof values.price === 'string' ? values.price.trim() : values.price;

      setValues({
        name: trimmedName,
        description: trimmedDescription,
        price: trimmedPrice,
      });

      const trimmedValues = {
        ...values,
        name: trimmedName,
        description: trimmedDescription,
        price: trimmedPrice,
      };

      const hasErrors = await validate(
        object().shape({
          name: string()
            .required(t('required', { ns: 'validation' })!)
            .min(TEXT_MIN, 'Минимум 2 символа')
            .max(TEXT_MAX, 'Максимум 50 символов'),
          price: string()
            .required(t('required', { ns: 'validation' })!)
            .matches(/^\d+$/, 'Допустимы только цифры'),
          description: string()
            .required(t('required', { ns: 'validation' })!)
            .min(TEXT_MIN, 'Минимум 2 символа')
            .max(TEXT_MAX, 'Максимум 50 символов'),
          endDate: date().required(t('required', { ns: 'validation' })!),
        }),
        trimmedValues
      );

      if (hasErrors) {
        return;
      }

      onSubmit({
        name: trimmedValues.name,
        price: trimmedValues.price,
        description: trimmedValues.description,
        endDate: trimmedValues.endDate!.toJSON(),
      });
    } catch (e) {}
  }, [onSubmit, t, validate, values, setValues]);

  const label = useMemo(() => {
    return type === DocTypes.CONTRACT ? 'договора' : 'документа';
  }, [type]);

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="name" className="block mb-1 text-sm font-medium">
              Предмет {label}
            </label>
            <Input
              id="name"
              value={values.name}
              name="name"
              onChange={(e) => onChange(e.target.value, 'name')}
              placeholder={`Введите предмет ${label}`}
              maxLength={TEXT_MAX}
              required
            />
            <span className="block min-h-[18px] text-red-500 text-xs mt-1">
              {errors.name || ''}
            </span>
          </div>
          <div>
            <label htmlFor="endDate" className="block mb-1 text-sm font-medium">
              Cрок {label}
            </label>
            <DatePicker
              value={values.endDate}
              name="endDate"
              onChange={onChange}
              placeholderText="Выберите срок"
              required={true}
              minDate={new Date()}
            />
            {errors.endDate && (
              <span className="text-red-500 text-xs mt-1">
                {errors.endDate}
              </span>
            )}
          </div>
          <div>
            <label htmlFor="price" className="block mb-1 text-sm font-medium">
              Сумма {label}
            </label>
            <Input
              id="price"
              value={values.price}
              onChange={onChangePrice}
              name="price"
              inputMode="numeric"
              autoComplete="off"
              placeholder={`Введите сумму ${label}`}
              required
            />
            <span className="block min-h-[18px] text-red-500 text-xs mt-1">
              {errors.price || ''}
            </span>
          </div>
        </div>
        <div>
          <label
            htmlFor="description"
            className="block mb-1 text-sm font-medium"
          >
            Краткое содержание
          </label>
          <Input
            id="description"
            value={values.description}
            onChange={(e) => onChange(e.target.value, 'description')}
            name="description"
            placeholder="Введите краткое содержание"
            maxLength={TEXT_MAX}
            required
          />
          <span className="block min-h-[18px] text-red-500 text-xs mt-1">
            {errors.description || ''}
          </span>
        </div>
      </Card>
      <div className="flex justify-end px-6">
        <Button onClick={onClickSubmit}>Продолжить</Button>
      </div>
    </div>
  );
}

export default memo(FormDoc);
